import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { usePreferencesStore } from '../store/preferencesStore';
import { getUserStatistics, getUserTransactions, updateUserProfile, type UserStatistics } from '../api/user';
import type { Transaction, PaginatedResponse } from '../types';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { NotificationSettings } from '../components/notifications/NotificationSettings';
import '../components/styles/ProfilePage.css';

export const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuthStore();
  const { displayName, preferredCurrency, setDisplayName, setPreferredCurrency } = usePreferencesStore();

  const [statistics, setStatistics] = useState<UserStatistics | null>(null);
  const [transactions, setTransactions] = useState<PaginatedResponse<Transaction> | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'history'>('profile');

  // Local state for display name input
  const [localDisplayName, setLocalDisplayName] = useState(displayName);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!user) return;

    // Fetch user statistics
    getUserStatistics(user.xrpl_address)
      .then((stats) => {
        setStatistics(stats);
        setLoadingStats(false);
      })
      .catch((err) => {
        console.error('Failed to fetch statistics:', err);
        setLoadingStats(false);
      });
  }, [user]);

  useEffect(() => {
    if (!user) return;

    // Fetch user transactions
    setLoadingTransactions(true);
    getUserTransactions(user.xrpl_address, currentPage, 20)
      .then((txs) => {
        setTransactions(txs);
        setLoadingTransactions(false);
      })
      .catch((err) => {
        console.error('Failed to fetch transactions:', err);
        setLoadingTransactions(false);
      });
  }, [user, currentPage]);

  const handleSaveProfile = async () => {
    if (!user) return;

    try {
      setSavingProfile(true);
      setError(null);

      // Update display name in preferences store
      setDisplayName(localDisplayName);

      // Update display name on backend
      await updateUserProfile(user.xrpl_address, { displayName: localDisplayName });

      // Update user in auth store
      updateUser({ username: localDisplayName });
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError('Failed to save profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCurrencyChange = (currency: string) => {
    if (currency === 'XRP' || currency === 'USD') {
      setPreferredCurrency(currency);
    }
  };

  if (!user) {
    return (
      <div className="profile-page">
        <div className="profile-error">
          <p>Please sign in to view your profile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1>My Profile</h1>
        <p className="profile-address">{user.xrpl_address}</p>
      </div>

      <div className="profile-tabs">
        <button
          className={`profile-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile & Settings
        </button>
        <button
          className={`profile-tab ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          Notifications
        </button>
        <button
          className={`profile-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Trading History
        </button>
      </div>

      {activeTab === 'profile' && (
        <div className="profile-content">
          {/* Statistics Section */}
          <section className="profile-section">
            <h2>Account Statistics</h2>
            {loadingStats ? (
              <div className="profile-loading">Loading statistics...</div>
            ) : statistics ? (
              <div className="profile-stats">
                <div className="stat-card">
                  <div className="stat-label">NFTs Owned</div>
                  <div className="stat-value">{statistics.nftsOwned}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Active Offers</div>
                  <div className="stat-value">{statistics.activeOffers}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Completed Sales</div>
                  <div className="stat-value">{statistics.completedSales}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Total Volume</div>
                  <div className="stat-value">{statistics.totalVolume.toLocaleString()} XRP</div>
                </div>
              </div>
            ) : (
              <div className="profile-error">Failed to load statistics</div>
            )}
          </section>

          {/* Settings Section */}
          <section className="profile-section">
            <h2>Profile Settings</h2>
            {error && <div className="profile-error-message">{error}</div>}
            <div className="profile-form">
              <div className="form-group">
                <label htmlFor="displayName">Display Name</label>
                <Input
                  id="displayName"
                  type="text"
                  value={localDisplayName}
                  onChange={(value) => setLocalDisplayName(value as string)}
                  placeholder="Enter display name"
                />
                <span className="form-hint">Max 50 characters</span>
              </div>

              <div className="form-group">
                <label htmlFor="currency">Preferred Currency</label>
                <Select
                  id="currency"
                  value={preferredCurrency}
                  onChange={(value) => handleCurrencyChange(value as string)}
                  options={[
                    { value: 'XRP', label: 'XRP' },
                    { value: 'USD', label: 'USD' },
                  ]}
                />
                <span className="form-hint">Currency to display prices in</span>
              </div>

              <button
                className="profile-save-button"
                onClick={handleSaveProfile}
                disabled={savingProfile}
              >
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </section>
        </div>
      )}

      {activeTab === 'notifications' && (
        <div className="profile-content">
          <section className="profile-section">
            <h2>Notification Preferences</h2>
            <NotificationSettings />
          </section>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="profile-content">
          <section className="profile-section">
            <h2>Trading History</h2>
            {loadingTransactions ? (
              <div className="profile-loading">Loading transactions...</div>
            ) : transactions && transactions.items.length > 0 ? (
              <>
                <div className="transaction-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>NFT Token ID</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Price</th>
                        <th>Transaction</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.items.map((tx) => (
                        <tr key={tx.id}>
                          <td>{new Date(tx.occurred_at).toLocaleDateString()}</td>
                          <td>
                            <span className={`tx-type tx-type-${tx.transaction_type.toLowerCase()}`}>
                              {tx.transaction_type}
                            </span>
                          </td>
                          <td className="tx-token-id">{tx.nft_token_id.slice(0, 12)}...</td>
                          <td className="tx-address">{tx.from_address ? `${tx.from_address.slice(0, 8)}...` : '-'}</td>
                          <td className="tx-address">{tx.to_address ? `${tx.to_address.slice(0, 8)}...` : '-'}</td>
                          <td>{tx.price_xrp ? `${tx.price_xrp.toLocaleString()} XRP` : '-'}</td>
                          <td>
                            <a
                              href={`https://livenet.xrpl.org/transactions/${tx.transaction_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tx-link"
                            >
                              View
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {transactions.pagination.pages > 1 && (
                  <div className="transaction-pagination">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="pagination-button"
                    >
                      Previous
                    </button>
                    <span className="pagination-info">
                      Page {currentPage} of {transactions.pagination.pages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(transactions.pagination.pages, p + 1))}
                      disabled={currentPage === transactions.pagination.pages}
                      className="pagination-button"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="profile-empty">No trading history found</div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};
